import { type FC } from "react";

const R = 38;
const CX = 44;
const CY = 48;
const CIRCUMFERENCE = Math.PI * R;
const STROKE_WIDTH = 5;
const DOT_RADIUS = 3.5;
const ARC_PATH = `M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`;

function getColor(pct: number): string {
  if (pct < 34) return "#E24B4A";
  if (pct < 67) return "#EF9F27";
  return "#639922";
}

function getDot(pct: number): { x: number; y: number } {
  const p = Math.max(0.01, Math.min(0.99, pct / 100));
  const angle = Math.PI * (1 - p);
  return {
    x: CX + R * Math.cos(angle),
    y: CY - R * Math.sin(angle),
  };
}

interface IArcGaugeProps {
  percentage: number;
  size?: "default" | "compact";
}

const VIEWBOX_TOP = 7;
const VIEWBOX_HEIGHT = 47;

const SIZES = {
  default: { width: 88, height: 47 },
  compact: { width: 72, height: 38 },
} as const;

export const ArcGauge: FC<IArcGaugeProps> = ({
  percentage,
  size = "default",
}) => {
  const pct = Math.max(0, Math.min(100, percentage));
  const color = getColor(pct);
  const dashOffset = CIRCUMFERENCE * (1 - pct / 100);
  const dot = getDot(pct);

  const dimensions = SIZES[size];

  return (
    <svg
      viewBox={`0 ${VIEWBOX_TOP} 88 ${VIEWBOX_HEIGHT}`}
      width={dimensions.width}
      height={dimensions.height}
      aria-label={`${Math.round(pct)}% chance`}
      style={{ flexShrink: 0 }}
    >
      <path
        d={ARC_PATH}
        fill="none"
        stroke="rgba(0,0,0,0.1)"
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
      />
      <path
        d={ARC_PATH}
        fill="none"
        stroke={color}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={dashOffset}
      />
      {pct > 1 && pct < 99 && (
        <circle cx={dot.x} cy={dot.y} r={DOT_RADIUS} fill={color} />
      )}
      <text
        x={CX}
        y={37}
        textAnchor="middle"
        fontSize="14"
        fontWeight="700"
        fill={color}
        fontFamily="inherit"
      >
        {Math.round(pct)}%
      </text>
      <text
        x={CX}
        y={47}
        textAnchor="middle"
        fontSize="8"
        fill={color}
        fontFamily="inherit"
        letterSpacing="0.5"
      >
        chance
      </text>
    </svg>
  );
};
